import Calendar from '@assets/icons/calendar';
import Clock from '@assets/icons/clock';
import { WebinarSchedule } from '@models/interfaces/mock_exam.interface';
import { Button, Checkbox, PopUp } from 'geniebook-ui/dist';
import React, { useEffect, useState } from 'react';
import './BookWebinar.css';
import { ErrorObject } from '@models/interfaces/global.interface';
import moment from 'moment';
import { PAGE_WEBINAR_PAYMENT } from '@utils/constants/mock-exam.constants';
import {
  currencySymbols,
  ICurrencySymbols,
} from '@utils/constants/mock-exam.constants';
import { levelName } from '@utils/helpers/levelName';
import { NumberToFormattedString } from '@utils/helpers/formatNumber';

const BookWebinar = ({
  scheduleList,
  setScheduleList,
  sortedKey,
  setError,
  setStep,
}: {
  scheduleList: { [key: number]: WebinarSchedule[] };
  setScheduleList: React.Dispatch<
    React.SetStateAction<{ [key: number]: WebinarSchedule[] }>
  >;
  sortedKey: string[];
  setError: React.Dispatch<React.SetStateAction<ErrorObject>>;
  setStep: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [isFormSubmit] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [localTimeZone, setLocalTimeZone] = useState<string>('');
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [showConflictPopup, setShowConflictPopUp] = useState<boolean>(false);
  const [conflictingSchedule, setConflictingSchedule] = useState<
    WebinarSchedule[][]
  >([]);

  // get the curreny from the first schedule in the list
  const firstSchedule: WebinarSchedule =
    scheduleList[parseInt(Object.keys(scheduleList)[0])][0];
  const currencySymbol =
    currencySymbols[firstSchedule.currency_code as keyof ICurrencySymbols];

  useEffect(() => {
    const momentObj = moment(new Date());
    setLocalTimeZone(momentObj.format('Z').replace(/[0:]/g, ''));
    hanldeScheduleStates(undefined);
  }, []);

  const cbScheduleOnChange = (schedule: WebinarSchedule) => {
    if (schedule.is_registered) return;
    if (schedule.will_register === true) {
      schedule.will_register = false;
    } else {
      schedule.will_register = true;
    }

    const newScheduleList = JSON.parse(JSON.stringify(scheduleList)); // recreate the object, to re-render
    setScheduleList(newScheduleList);
    hanldeScheduleStates(schedule);
  };

  /*
   * Reacalculate total price
   * Check for conflicting schedule
   */
  const hanldeScheduleStates = (
    clickedSchdule: WebinarSchedule | undefined,
  ) => {
    let totalPrice = 0;
    const selectedSchedule: WebinarSchedule[] = [];
    for (const level_id in scheduleList) {
      if (!Object.prototype.hasOwnProperty.call(scheduleList, 'level_id'))
        continue;
      for (const schedule of scheduleList[level_id]) {
        if (schedule.will_register === true) {
          totalPrice += schedule.price;
          selectedSchedule.push(schedule);
        } else if (schedule.is_registered === true) {
          selectedSchedule.push(schedule);
        }
      }
    }

    if (clickedSchdule !== undefined) {
      // check if there is conflicting schedule
      // sort the selected schedules
      selectedSchedule.sort((a, b) => {
        if (a.class_date !== b.class_date) {
          return a.class_date.localeCompare(b.class_date);
        } else {
          return a.start_time.localeCompare(b.start_time);
        }
      });

      // find for conflicts
      const tempConflictingSchedule: WebinarSchedule[][] = [];
      let currentConflicts: WebinarSchedule[] = [];
      let showSnackBar = false;
      for (let i = 1; i < selectedSchedule.length; i++) {
        if (
          selectedSchedule[i - 1].class_date ===
            selectedSchedule[i].class_date &&
          selectedSchedule[i - 1].end_time >= selectedSchedule[i].start_time
        ) {
          if (currentConflicts.length === 0) {
            currentConflicts.push(selectedSchedule[i - 1]);
            if (selectedSchedule[i - 1].class_id == clickedSchdule?.class_id)
              showSnackBar = true;
          }
          currentConflicts.push(selectedSchedule[i]);
          if (selectedSchedule[i].class_id == clickedSchdule?.class_id)
            showSnackBar = true;
        } else {
          if (currentConflicts.length > 0) {
            tempConflictingSchedule.push([...currentConflicts]);
            currentConflicts = [];
          }
        }
      }
      // check last batch
      if (currentConflicts.length > 0) {
        tempConflictingSchedule.push([...currentConflicts]);
        currentConflicts = [];
      }

      if (showSnackBar) {
        setError({
          error: true,
          error_msg: 'Conflicting schedules detected',
          error_code: '',
          error_state: 'warning',
        });
      }
      setConflictingSchedule(tempConflictingSchedule);
    }

    setTotalPrice(totalPrice);
    setIsButtonDisabled(totalPrice <= 0);
  };

  const submitBookWebinar = async () => {
    setIsButtonDisabled(true);
    if (conflictingSchedule.length != 0) {
      setShowConflictPopUp(true);
      return;
    }
    setStep(PAGE_WEBINAR_PAYMENT);
  };

  const convertTZ = (dateString: string, format: string) => {
    const dateStringMoment = moment(dateString);

    // for detecting timezone offset https://stackoverflow.com/a/8090836
    const timezone = parseInt(
      (-(new Date().getTimezoneOffset() / 60)).toString(),
    );
    dateStringMoment.hour(dateStringMoment.hour() - 8 + timezone);

    return dateStringMoment.format(format);
  };

  return (
    <>
      <div className="font-black text-primary-navyblue text-xl leading-[32px] mb-4">
        Select webinar sessions
      </div>
      <p className="text-neutral-pencil mb-8">
        All timings are in your local time{' '}
        {localTimeZone !== '' ? '(GMT ' + localTimeZone + ')' : ''}
      </p>
      <div className="max-h-[348px] overflow-auto -ml-2 pl-2 flex flex-col gap-6 max-sm:max-h-[250px]">
        {sortedKey.length != 0 ? (
          sortedKey.map(key => (
            <div key={key}>
              <div className="mock-exam-student-name">
                {levelName(parseInt(key), false) + ''.toUpperCase()}{' '}
              </div>
              {scheduleList[parseInt(key)].map((schedule: WebinarSchedule) => (
                <div
                  key={schedule.class_id}
                  className={
                    'mock-exam-schedule-container' +
                    (schedule.is_registered ? ' enrolled' : '')
                  }
                >
                  <div className="mock-exam-schedule-checkbox-container">
                    <Checkbox
                      className="cursor-pointer"
                      variant={'light'}
                      isChecked={
                        schedule.will_register === true ||
                        schedule.is_registered === true
                      }
                      isValidation={false}
                      onChange={() => cbScheduleOnChange(schedule)}
                      label={''}
                      disabled={isFormSubmit || schedule.is_registered}
                    />
                  </div>
                  <div
                    className="mock-exam-schedule-desc"
                    onClick={() => cbScheduleOnChange(schedule)}
                  >
                    <div className="flex justify-start items-center">
                      <p className="mock-exam-schedule-title">
                        {schedule.level_name} {schedule.subject_name}{' '}
                        {'Webinar'}
                      </p>
                      <span
                        className={
                          'px-2 py-[2px] ml-2 mb-1 rounded-2xl text-xs font-bold ' +
                          (schedule.is_registered
                            ? 'bg-neutral-dust text-neutral-whiteboard'
                            : 'bg-primary-apricot-light text-primary-apricot-dark')
                        }
                      >
                        {schedule.is_registered
                          ? 'Registered'
                          : currencySymbol +
                            NumberToFormattedString(schedule.price)}
                      </span>
                    </div>
                    <div className="mock-exam-schedule-datetime !text-[13px] max-lg:!text-sm">
                      <div className="mock-exam-schedule-date">
                        <Calendar />
                        <span>{schedule.class_date_display}</span>
                      </div>
                      <div className="mock-exam-schedule-time">
                        <Clock />
                        <span>
                          {convertTZ(
                            schedule.class_date + ' ' + schedule.start_time,
                            'LT',
                          ) +
                            ' - ' +
                            convertTZ(
                              schedule.class_date + ' ' + schedule.end_time,
                              'LT',
                            )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="mb-8 text-neutral-pencil text-center">
            {' '}
            No available webinar{' '}
          </div>
        )}
      </div>
      <Button
        variant="primary"
        onClick={submitBookWebinar}
        className="max-w-full px-5"
        disabled={isButtonDisabled}
      >
        Book sessions
        {` (Total ${currencySymbol}${NumberToFormattedString(totalPrice)})`}
      </Button>

      {showConflictPopup === true && (
        <PopUp
          title={'Schedule Notice'}
          description={
            <div className="text-neutral-pencil flex flex-col gap-2">
              <div>These webinars are happening at the same time.</div>
              <ul className="list-disc list-outside marker:text-primary-apricot flex flex-col items-start gap-2 text-left pl-4 max-h-[200px] overflow-scroll">
                {conflictingSchedule.map((conflict: WebinarSchedule[]) => (
                  <li key={conflict[0].class_id + conflict[1].class_id}>
                    {conflict.map((schedule: WebinarSchedule, index) => (
                      <>
                        {`${schedule.level_name} ${schedule.subject_name} Webinar`}
                        {index !== conflict.length - 1 && <span> &amp; </span>}
                      </>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          }
          buttonGroup={
            <>
              <Button
                variant="primary"
                onClick={() => setStep(PAGE_WEBINAR_PAYMENT)}
                className="max-w-full"
              >
                I acknowledge
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowConflictPopUp(false)}
                className="max-w-full"
              >
                Back to schedule
              </Button>
            </>
          }
          onClose={() => {}}
          disableBodyScroll={true}
          width="368px"
        />
      )}
    </>
  );
};

export default BookWebinar;
